import { api } from "@/state/services";
import { chaveUsuario } from "@/utils/minhas-metas";

/**
 * Utilitários da "Jornada diária" (Minhas Metas gamificado) e da "Minha Jornada" (dashboard).
 *
 * A autoridade sobre "já viu hoje?" é SEMPRE o servidor (GET metas/jornada usa o dia de
 * America/Sao_Paulo). O localStorage aqui é só cache para não chamar a API a cada navegação
 * (o layout main.vue é remontado a cada troca de rota) e para não "piscar" a jornada.
 */

const VISTA_PREFIX = "jornada_vista:";
const TOUR_LOCAL_PREFIX = "onboarding_done:";

/** Dia de hoje em America/Sao_Paulo ('Y-m-d'). Só para cache local — o servidor decide. */
export function hojeBrt() {
  try {
    // en-CA formata como AAAA-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch (e) {
    const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  }
}

export function lerVistaLocal(user) {
  const chave = chaveUsuario(user);
  if (!chave) return null;
  try {
    return localStorage.getItem(VISTA_PREFIX + chave);
  } catch (e) {
    return null;
  }
}

export function gravarVistaLocal(user, dia) {
  const chave = chaveUsuario(user);
  if (!chave) return;
  try {
    localStorage.setItem(VISTA_PREFIX + chave, dia || hojeBrt());
  } catch (e) {
    // sem storage: o servidor continua sendo a fonte da verdade
  }
}

/** O tour de boas-vindas (onboarding-modal.vue) já foi concluído? */
export function tourConcluido(user) {
  if (!user) return false;
  if (user.onboarding_done === "yes") return true;
  try {
    return localStorage.getItem(TOUR_LOCAL_PREFIX + user.id) === "yes";
  } catch (e) {
    return false;
  }
}

/**
 * Algum fluxo global de precedência maior está (ou deve estar) em cena?
 * Mesmos bloqueios do tour (onboarding-modal.vue::blockedByRequiredFlow) e do wizard de metas:
 * RegionalCpfModal, AfiliadosRegulamentoModal, pendências de cadastro/documentos/contratos,
 * replay do tour e o wizard de metas aberto manualmente.
 */
export function fluxoGlobalBloqueado(account, route) {
  const user = account && account.user;
  if (!user) return true;
  const path = (route && route.path) || "";
  if (
    path === "/atualizacad" ||
    path === "/documents" ||
    path.indexOf("/contracts/") === 0 ||
    path.indexOf("/login") === 0 ||
    path === "/logout"
  ) {
    return true;
  }
  if (user.atualizacad === "pending") return true;
  if (user.documents === "pending") return true;
  if (user.contracts && user.contracts.length > 0) return true;
  if (Number(user.regional) === 1 && !user.cpf_regional) return true;
  if (account.afiliadosRegulamentoPendingPath) return true;
  if (account.onboardingReplay) return true;
  if (account.metasWizard) return true;
  return false;
}

/**
 * Estado da jornada de hoje, com cache no Vuex por usuário + dia (BRT local).
 * @returns {Promise<object>} corpo do GET metas/jornada
 */
export function carregarJornada(store, opcoes) {
  const force = !!(opcoes && opcoes.force);
  const account = store.state.account;
  const key = chaveUsuario(account.user);
  const dia = hojeBrt();
  const atual = account.jornadaEstado;

  if (!force && atual && atual.key === key && atual.dia === dia) {
    return Promise.resolve(atual.data);
  }

  return api.get("metas/jornada").then((response) => {
    const data = response && response.data;
    if (!data || data.status !== "success") {
      throw new Error((data && data.message) || "Jornada indisponível.");
    }
    store.commit("account/setJornadaEstado", { key, dia, data });
    return data;
  });
}

/** Atualiza só alguns campos do estado em cache (ex.: depois do PUT/POST). */
export function mesclarJornada(store, parcial) {
  const account = store.state.account;
  const atual = account.jornadaEstado;
  const key = chaveUsuario(account.user);
  if (!atual || atual.key !== key) return;
  store.commit("account/setJornadaEstado", { ...atual, data: { ...atual.data, ...parcial } });
}

export function prefereMenosMovimento() {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (e) {
    return false;
  }
}

/**
 * Anima um número de `de` até `ate` (easeOutCubic). Chama onFrame(valor) a cada quadro.
 * Com prefers-reduced-motion (ou sem requestAnimationFrame) pula direto para o valor final.
 * @returns {Function} cancela a animação
 */
export function animarNumero(de, ate, duracao, onFrame) {
  const inicio = Number(de) || 0;
  const fim = Number(ate) || 0;
  if (prefereMenosMovimento() || typeof window.requestAnimationFrame !== "function" || inicio === fim) {
    onFrame(fim);
    return () => {};
  }
  let cancelado = false;
  let t0 = null;
  const passo = (t) => {
    if (cancelado) return;
    if (t0 === null) t0 = t;
    const p = Math.min(1, (t - t0) / duracao);
    const e = 1 - Math.pow(1 - p, 3);
    onFrame(inicio + (fim - inicio) * e);
    if (p < 1) window.requestAnimationFrame(passo);
  };
  window.requestAnimationFrame(passo);
  return () => {
    cancelado = true;
  };
}

/** Imagens da jornada (webpack resolve e gera hash). */
export const IMG_BRASAO = {
  consultor: require("@/assets/images/jornada/brasao-consultor.png"),
  "consultor-lider": require("@/assets/images/jornada/brasao-consultor-lider.png"),
  homeoffice: require("@/assets/images/jornada/brasao-home-office.png"),
  executiva: require("@/assets/images/jornada/brasao-executiva.png"),
  "gerente-regional": require("@/assets/images/jornada/brasao-gerente-regional.png"),
  "gerente-master": require("@/assets/images/jornada/brasao-gerente-master.png"),
  diretor: require("@/assets/images/jornada/brasao-diretor-estado.png"),
};

export const IMG_QUALIFICACAO = {
  5: require("@/assets/images/jornada/qualificacao-5.png"),
  7: require("@/assets/images/jornada/qualificacao-7.png"),
  10: require("@/assets/images/jornada/qualificacao-10.png"),
};

export const IMG_PIN = {
  600: require("@/assets/images/jornada/pin-600.png"),
  1000: require("@/assets/images/jornada/pin-1000.png"),
  2000: require("@/assets/images/jornada/pin-2000.png"),
  3000: require("@/assets/images/jornada/pin-3000.png"),
  4000: require("@/assets/images/jornada/pin-4000.png"),
  5000: require("@/assets/images/jornada/pin-5000.png"),
  6000: require("@/assets/images/jornada/pin-6000.png"),
};

export const IMG_FORMADOR = {
  5: require("@/assets/images/jornada/formador-5.png"),
  10: require("@/assets/images/jornada/formador-10.png"),
  15: require("@/assets/images/jornada/formador-15.png"),
  20: require("@/assets/images/jornada/formador-20.png"),
  25: require("@/assets/images/jornada/formador-25.png"),
  30: require("@/assets/images/jornada/formador-30.png"),
  40: require("@/assets/images/jornada/formador-40.png"),
  50: require("@/assets/images/jornada/formador-50.png"),
};

export const ROTULO_FAIXA = {
  cinza: "Sem qualificação",
  bronze: "Bronze",
  prata: "Prata",
  ouro: "Ouro",
};
